"use client";

import { c as _c } from "react/compiler-runtime";
import { ReactNode, createContext, useContext } from "react";
import type { AnalyticsColumn, AnalyticsGroup, AnalyticsGroupedResponse, AnalyticsRow, AnalyticsSummary, PaginationContextValue } from "./types";

// ============================================================================
// Context Types
// ============================================================================

export interface AnalyticsGroupedTableContextValue<TRow extends AnalyticsRow = AnalyticsRow, TSummary extends AnalyticsSummary = AnalyticsSummary> {
  // Data
  visibleColumns: AnalyticsColumn<TRow, TSummary>[];
  sortedData: AnalyticsGroupedResponse<TRow, TSummary> | undefined;
  isGrouped: boolean;
  itemLabel: string;
  showGrandTotals: boolean;
  grandTotalsPosition: "top" | "bottom";
  isTrailingGroup?: (group: AnalyticsGroup<TRow, TSummary>) => boolean;
  emptyMessage: string;

  // Expansion
  expandedGroups: Set<string>;
  toggleGroup: (key: string) => void;

  // Sorting
  sortFilters: {
    sortBy: string;
    sortDirection: "asc" | "desc";
  };
  setSortFilters: (update: {
    sortBy: string;
    sortDirection: "asc" | "desc";
  } | ((prev: {
    sortBy: string;
    sortDirection: "asc" | "desc";
  }) => {
    sortBy: string;
    sortDirection: "asc" | "desc";
  })) => void;

  // Callbacks
  onRowClick?: (row: TRow, groupKey?: string) => void;
  getRowKey?: (row: TRow) => string;
  renderSummaryLabel?: (label: string, isGrandTotal: boolean) => ReactNode;
  renderGroupHeader?: (group: AnalyticsGroup<TRow, TSummary>, isExpanded: boolean, onToggle: () => void) => ReactNode;

  // Pagination (optional - only present when pagination prop is set)
  pagination?: PaginationContextValue;
}

// ============================================================================
// Context
// ============================================================================

const AnalyticsGroupedTableContext = createContext<AnalyticsGroupedTableContextValue<AnalyticsRow, AnalyticsSummary> | undefined>(undefined);

// ============================================================================
// Provider
// ============================================================================

interface AnalyticsGroupedTableProviderProps<TRow extends AnalyticsRow, TSummary extends AnalyticsSummary> {
  children: ReactNode;
  value: AnalyticsGroupedTableContextValue<TRow, TSummary>;
}
export function AnalyticsGroupedTableProvider(t0) {
  const $ = _c(3);
  const {
    children,
    value
  } = t0;
  const t1 = value as unknown as AnalyticsGroupedTableContextValue<AnalyticsRow, AnalyticsSummary>;
  let t2;
  if ($[0] !== children || $[1] !== t1) {
    t2 = <AnalyticsGroupedTableContext.Provider value={t1}>{children}</AnalyticsGroupedTableContext.Provider>;
    $[0] = children;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}

// ============================================================================
// Hook
// ============================================================================

export function useAnalyticsGroupedTable() {
  const context = useContext(AnalyticsGroupedTableContext);
  if (!context) {
    throw new Error("useAnalyticsGroupedTable must be used within an AnalyticsGroupedTableProvider");
  }
  return context as unknown as AnalyticsGroupedTableContextValue<TRow, TSummary>;
}